import PropTypes from 'prop-types'
import React from 'react'
import glamorous from 'glamorous'
import content from 'pp-react-l10n'
import * as AddressUtils from '../utils/address-utils'
import * as CardActions from '../actions/card'
import { StyleSheet, css, rtlConvertInline } from 'lib/paypalphrodite'
import classNames from 'classnames'
import enRTRStrings from '../../../../config/enRTRStrings'
import { isDw2Repaint } from '../../experiments/dw2Repaint'
import {
  Button,
  CaptionText,
  DropdownMenu,
  v2grey600,
} from '@paypalcorp/pp-react'
import { tracking } from '../../lib/analytics/mParticle'
import Events from '../../lib/analytics/eventTags'
import { greyBackgroundAndSmaller } from 'lib/styles/media-queries'

let i18n = content('widgets/addressDropdown')

const Container = glamorous.div({
  margin: '1rem 2rem',
  [greyBackgroundAndSmaller]: {
    margin: '1rem 0',
  },
})

const styles = StyleSheet.create({
  dropDownContainer: {
    margin: '10px 0 0',
    position: 'relative',
  },
  // dropDown and arrow styles are copied to be inline with other select drop downs and components.
  // styles. Taken from https://github.paypal.com/react/guest-payment/blob/master/src/components/CountrySelection.js#L10
  dropDown: {
    padding: '0 1.5rem 0 0',
    textIndent: 10,
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
    '@-moz-document url-prefix()': {
      textIndent: 2.5, // FireFox textIndent @10px is too much - Only way to apply indent to FF specifically
    },
    '::-ms-expand': { display: 'none' }, // appearance for MS since deprecated in IE
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    cursor: 'pointer',
    pointerEvents: 'none', // needed to allow user to click on/around icon and trigger dropDown
  },
})

class AddressDropdown extends React.Component {
  static propTypes = {
    addresses: PropTypes.array.isRequired,
    addressEntryFormat: PropTypes.object.isRequired,
    selectedAddress: PropTypes.object.isRequired,
    handleAddAddress: PropTypes.func.isRequired,
    handleSelectAddress: PropTypes.func.isRequired,
    selectElementId: PropTypes.string.isRequired,
    yourAddressContentKey: PropTypes.string.isRequired,
    addNewAddressContentKey: PropTypes.string.isRequired,
    shouldRenderLabel: PropTypes.bool.isRequired,
    hasNoAddressNeededOption: PropTypes.bool.isRequired,
    shakeBillingLabel: PropTypes.bool,
    dispatch: PropTypes.func,
    dir: PropTypes.string.isRequired,
    showENSubTextRTR: PropTypes.bool,
  }

  renderDropdownOptions = () => {
    return this.props.addresses
      .map(address => {
        const selectedAddressSingleLine = this.convertAddressToSingleLine(
          address
        )
        // Do not show invalid address
        if (isDw2Repaint()) {
          return selectedAddressSingleLine
            ? {
                value: address.id,
                primaryText: selectedAddressSingleLine,
              }
            : undefined
        }
        return selectedAddressSingleLine ? (
          <option key={address.id} id={address.id} value={address.id}>
            {selectedAddressSingleLine}
          </option>
        ) : null
      })
      .filter(option => option !== undefined) // remove invalid addresses
  }

  convertAddressToSingleLine = address => {
    const ellipsis = i18n('label.ellipsis')

    const griffinMappedAddress = {
      address_line_1:
        (address.addressLine1 && address.addressLine1.value) ||
        address.address_line_1,
      address_line_2:
        (address.addressLine2 && address.addressLine2.value) ||
        address.address_line_2,
      admin_area_2:
        (address.city && address.city.value) || address.admin_area_2,
      admin_area_1:
        (address.state && address.state.value) || address.admin_area_1,
      postal_code:
        (address.zipcode && address.zipcode.value) || address.postal_code,
      country_code: address.countryCode || address.country_code,
    }
    const singleLine = AddressUtils.formatAddressToSingleLine(
      griffinMappedAddress
    )

    return singleLine.length > 75
      ? singleLine.substring(0, 75) + ellipsis
      : singleLine
  }

  handleChange = event => {
    if (event.target.value === 'handleAddAddress') {
      return this.props.handleAddAddress()
    }

    this.props.handleSelectAddress(event.target.value)
  }

  handleClick = () => {
    this.props.handleAddAddress()
    tracking(Events.p2p_review_shipping_address_pressed())
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.shakeBillingLabel && !this.props.shakeBillingLabel) {
      setTimeout(() => {
        this.props.dispatch(
          CardActions.handleNoBillingAddress(nextProps.shakeBillingLabel)
        )
      }, 2000)
    }
  }

  render() {
    const {
      yourAddressContentKey,
      selectElementId,
      shouldRenderLabel,
      addresses,
      selectedAddress,
      hasNoAddressNeededOption,
      addNewAddressContentKey,
      dir,
      shakeBillingLabel,
      showENSubTextRTR,
    } = this.props

    if (isDw2Repaint()) {
      return (
        <Container>
          {addresses.length > 0 && (
            <DropdownMenu
              id={selectElementId}
              label={
                <>
                  {i18n(yourAddressContentKey)}
                  {showENSubTextRTR &&
                    `(${enRTRStrings[yourAddressContentKey]})`}
                </>
              }
              options={[
                ...(hasNoAddressNeededOption
                  ? [
                      {
                        value: '',
                        primaryText: i18n('label.noAddressNeeded'),
                      },
                    ]
                  : []),
                ...this.renderDropdownOptions(),
                {
                  value: 'handleAddAddress',
                  primaryText: i18n(addNewAddressContentKey),
                },
              ]}
              data-nemo="add-shipping-address"
              value={selectedAddress.id}
              onChange={this.handleChange}
              onClick={() =>
                tracking(Events.p2p_review_shipping_address_pressed())
              }
            />
          )}
          {addresses.length === 0 && (
            <>
              <Button
                tertiary={true}
                type="button"
                style={{ padding: 0 }}
                data-nemo="add-fi-btn"
                onClick={this.handleClick}
              >
                {i18n(addNewAddressContentKey)}
              </Button>
              {showENSubTextRTR && (
                <CaptionText style={{ color: v2grey600 }}>
                  {enRTRStrings[addNewAddressContentKey]}
                </CaptionText>
              )}
            </>
          )}
        </Container>
      )
    }
    return (
      <glamorous.Div margin="1em auto" maxWidth="398px">
        {shouldRenderLabel && addresses.length > 0 && (
          <>
            <glamorous.Label
              htmlFor={selectElementId}
              className="vx_text-5"
              textAlign="left"
              marginBottom={showENSubTextRTR ? '0' : '1em'}
              fontWeight="500"
              display="block"
            >
              {i18n(yourAddressContentKey)}
            </glamorous.Label>
            {showENSubTextRTR && (
              <glamorous.Label
                htmlFor={selectElementId}
                className="vx_text-5"
                textAlign="left"
                marginBottom="1em"
                fontWeight="400"
                fontSize="14px"
                color="#687173"
                display="block"
              >
                {enRTRStrings[yourAddressContentKey]}
              </glamorous.Label>
            )}
          </>
        )}
        {addresses.length > 0 && (
          <div className={css(styles.dropDownContainer)}>
            {/* disabling linting requirement, but should be up to the dev what is needed */
            /* eslint-disable-next-line jsx-a11y/no-onchange */}
            <select
              id={selectElementId}
              className={classNames('vx_form-control', css(styles.dropDown))}
              data-nemo="add-shipping-address"
              value={selectedAddress.id}
              onChange={this.handleChange}
              aria-label={shouldRenderLabel ? '' : i18n(yourAddressContentKey)}
            >
              {hasNoAddressNeededOption && (
                <option value="">{i18n('label.noAddressNeeded')}</option>
              )}
              {this.renderDropdownOptions()}
              <option value="handleAddAddress">
                {i18n(addNewAddressContentKey)}
              </option>
            </select>
            <label
              htmlFor={selectElementId}
              style={dir === 'rtl' ? { left: 10 } : { right: 10 }}
              className={`icon icon-arrow-down-small ${css(
                rtlConvertInline(styles.arrow)
              )}`}
            />
          </div>
        )}
        {addresses.length === 0 && (
          <>
            <glamorous.Label
              className={shakeBillingLabel && 'animated shake'}
              onClick={this.handleClick}
              fontWeight="500"
              color="#0070ba"
              cursor="pointer"
            >
              {i18n(addNewAddressContentKey)}
            </glamorous.Label>
            {showENSubTextRTR && (
              <glamorous.Label fontWeight="500" fontSize="14px" color="#687173">
                {enRTRStrings[addNewAddressContentKey]}
              </glamorous.Label>
            )}
          </>
        )}
      </glamorous.Div>
    )
  }
}

export default AddressDropdown
